import React, { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { X, Hash, Plus, Check, Users } from 'lucide-react'
import { toast } from 'sonner'

/**
 * Channel directory — modal showing all public channels in the workspace.
 *
 * Two affordances in one modal:
 *   1. Create new channel (form at top)
 *   2. Browse + join existing channels (list below)
 *
 * Member counts and joined status are derived from a single batched query
 * against team_conversation_participants. Workspaces typically have well
 * under 50 channels, so a single table scan is fine.
 */

const CHANNEL_NAME_RE = /^[a-z0-9-]+$/
const CHANNEL_NAME_MAX = 30
const CHANNEL_DESC_MAX = 200

function validateChannelName(name: string): string | null {
  const trimmed = name.trim()
  if (trimmed.length === 0) return 'Channel name is required'
  if (trimmed.length > CHANNEL_NAME_MAX) return `Max ${CHANNEL_NAME_MAX} characters`
  if (!CHANNEL_NAME_RE.test(trimmed)) return 'Use lowercase letters, numbers, and hyphens only'
  return null
}

interface DirectoryRow {
  id: string
  name: string
  description: string | null
  memberCount: number
  isJoined: boolean
}

export default function ChannelDirectory({
  onClose,
  onChannelJoin,
  onChannelCreate,
}: {
  onClose: () => void
  onChannelJoin: (channelName: string) => void
  onChannelCreate: (channelName: string) => void
}) {
  const { workspace, profile } = useAuth()
  const queryClient = useQueryClient()

  const [newName, setNewName] = useState('')
  const [newDesc, setNewDesc] = useState('')
  const [creating, setCreating] = useState(false)
  const [joiningId, setJoiningId] = useState<string | null>(null)

  const nameError = newName ? validateChannelName(newName) : null

  // Fetch all channels in workspace + participants (in a second query) so we
  // can compute member counts and joined status client-side. N=channels and
  // M=participants are both small (<50/100); single scan each is fine.
  const { data: channels = [], isLoading } = useQuery<DirectoryRow[]>({
    queryKey: ['team-channels-directory', workspace?.id, profile?.id],
    enabled: !!workspace?.id && !!profile?.id,
    queryFn: async () => {
      if (!workspace?.id || !profile?.id) return []
      const { data: rows, error } = await supabase
        .from('team_conversations')
        .select('id, name, description')
        .eq('workspace_id', workspace.id)
        .eq('type', 'channel')
        .order('name', { ascending: true })
      if (error) throw error
      const channelRows = rows || []
      if (channelRows.length === 0) return []

      const ids = channelRows.map(r => r.id)
      const { data: parts, error: pErr } = await supabase
        .from('team_conversation_participants')
        .select('team_conversation_id, user_id')
        .in('team_conversation_id', ids)
      if (pErr) throw pErr

      const counts: Record<string, number> = {}
      const joinedSet = new Set<string>()
      ;(parts || []).forEach(p => {
        counts[p.team_conversation_id] = (counts[p.team_conversation_id] || 0) + 1
        if (p.user_id === profile.id) joinedSet.add(p.team_conversation_id)
      })

      return channelRows.map(r => ({
        id: r.id,
        name: r.name as string,
        description: r.description,
        memberCount: counts[r.id] || 0,
        isJoined: joinedSet.has(r.id),
      }))
    },
  })

  const handleCreate = async () => {
    if (!workspace?.id || !profile?.id) return
    const err = validateChannelName(newName)
    if (err) { toast.error(err); return }
    const trimmedName = newName.trim()
    if (channels.some(c => c.name === trimmedName)) {
      toast.error(`#${trimmedName} already exists`)
      return
    }
    setCreating(true)
    try {
      const { data: created, error: cErr } = await supabase
        .from('team_conversations')
        .insert({
          workspace_id: workspace.id,
          type: 'channel',
          name: trimmedName,
          description: newDesc.trim() || null,
          created_by: profile.id,
        })
        .select('id, name')
        .single()
      if (cErr) throw cErr
      // Auto-join creator
      const { error: pErr } = await supabase
        .from('team_conversation_participants')
        .insert({ team_conversation_id: created.id, user_id: profile.id })
      if (pErr) throw pErr
      queryClient.invalidateQueries({ queryKey: ['team-channels-directory'] })
      queryClient.invalidateQueries({ queryKey: ['team-joined-channels'] })
      queryClient.invalidateQueries({ queryKey: ['team-chat-unread'] })
      toast.success(`Created #${created.name}`)
      onChannelCreate(created.name as string)
    } catch (e: any) {
      toast.error(e?.message || 'Failed to create channel')
    } finally {
      setCreating(false)
    }
  }

  const handleJoin = async (row: DirectoryRow) => {
    if (!profile?.id) return
    setJoiningId(row.id)
    try {
      const { error } = await supabase
        .from('team_conversation_participants')
        .insert({ team_conversation_id: row.id, user_id: profile.id })
      if (error) throw error
      queryClient.invalidateQueries({ queryKey: ['team-channels-directory'] })
      queryClient.invalidateQueries({ queryKey: ['team-joined-channels'] })
      queryClient.invalidateQueries({ queryKey: ['team-chat-unread'] })
      toast.success(`Joined #${row.name}`)
      onChannelJoin(row.name)
    } catch (e: any) {
      toast.error(e?.message || 'Failed to join channel')
    } finally {
      setJoiningId(null)
    }
  }

  const sortedChannels = useMemo(() => {
    // 'general' always pinned to top, then alpha.
    return [...channels].sort((a, b) => {
      if (a.name === 'general') return -1
      if (b.name === 'general') return 1
      return a.name.localeCompare(b.name)
    })
  }, [channels])

  const inp: React.CSSProperties = {
    width: '100%',
    padding: '9px 12px',
    background: 'var(--field-bg)',
    border: '1px solid var(--border-default)',
    borderRadius: 9,
    fontSize: 13,
    color: 'var(--ink-primary)',
    outline: 'none',
    boxSizing: 'border-box',
  }

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 24 }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.15 }}
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 20, width: '100%', maxWidth: 560, maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>Channels</h2>
            <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: '3px 0 0' }}>Browse, join, or create channels for your team.</p>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 4, display: 'flex' }}>
            <X style={{ width: 18, height: 18 }} />
          </button>
        </div>

        <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 10px' }}>Create new channel</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--ink-tertiary)', fontSize: 14, fontWeight: 600, pointerEvents: 'none' }}>#</span>
              <input
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="marketing"
                maxLength={CHANNEL_NAME_MAX}
                style={{ ...inp, paddingLeft: 26 }}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'}
              />
            </div>
            {nameError && (
              <p style={{ fontSize: 11, color: 'var(--danger)', margin: 0 }}>{nameError}</p>
            )}
            <input
              value={newDesc}
              onChange={e => setNewDesc(e.target.value.slice(0, CHANNEL_DESC_MAX))}
              placeholder="What's this channel about? (optional)"
              maxLength={CHANNEL_DESC_MAX}
              style={inp}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={handleCreate}
                disabled={creating || !!nameError || !newName.trim()}
                className="btn-primary"
              >
                <Plus style={{ width: 14, height: 14 }} /> {creating ? 'Creating…' : 'Create channel'}
              </button>
            </div>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 0' }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '4px 24px 8px' }}>
            {isLoading ? 'Loading…' : `${sortedChannels.length} channel${sortedChannels.length === 1 ? '' : 's'}`}
          </p>
          {sortedChannels.length === 0 && !isLoading && (
            <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '12px 24px' }}>No channels yet. Create the first one above.</p>
          )}
          {sortedChannels.map(c => (
            <div
              key={c.id}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 24px', cursor: 'default' }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--hover-bg)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <Hash style={{ width: 16, height: 16, color: 'var(--ink-tertiary)', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</p>
                {c.description && (
                  <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: '2px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.description}</p>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--ink-tertiary)', fontSize: 11, flexShrink: 0 }}>
                <Users style={{ width: 12, height: 12 }} />
                <span>{c.memberCount}</span>
              </div>
              {c.isJoined ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, color: 'var(--positive)', background: 'var(--positive-dim)', padding: '4px 10px', borderRadius: 100, flexShrink: 0 }}>
                  <Check style={{ width: 12, height: 12 }} /> Joined
                </span>
              ) : (
                <button
                  onClick={() => handleJoin(c)}
                  disabled={joiningId === c.id}
                  style={{ padding: '5px 12px', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 100, color: 'var(--accent-text)', fontSize: 12, fontWeight: 600, cursor: joiningId === c.id ? 'default' : 'pointer', flexShrink: 0 }}
                >
                  {joiningId === c.id ? 'Joining…' : 'Join'}
                </button>
              )}
            </div>
          ))}
        </div>
      </motion.div>
    </motion.div>
  )
}
